/**
 * "Se lønnsarter" for one person (manager redesign, 2026-10-09): every line the report gives them,
 * with the arithmetic, the lønnsart the file writes, who pays (the "(NAV)" lines), the unit (hours,
 * or days for sickness), and the report's reasons. Every figure is the report's own (lines.ts); this
 * file only lays them out.
 */
import { T } from '@/i18n';
import { num } from '@/ui/format';
import { Fact } from '@/ui/page';
import type { PayLine, PayPart } from './lines';
import { LINE_NOTES } from './run';

export const kr = (n: number) => Math.round(n).toLocaleString('nb-NO');
// the report sends rates as strings ("204.79"), which toLocaleString left as they were (settings test, 2026-09-21)
export const kr2 = (n: number | string) => Number(n).toLocaleString('nb-NO', { maximumFractionDigits: 2 });

const KIND: Record<PayPart['kind'], string> = {
  base: 'Grunnlønn',
  supplement: 'Tillegg',
  overtime: 'Overtid',
  absence: 'Fravær',
};

/** "10 t × 200", "3 dager × 1 450", or "4 t" when the line has no rate. */
export const workings = (p: PayPart) =>
  `${num(p.hours)} ${p.unit === 'dager' ? T('dager') : 't'}${p.rate > 0 ? ` × ${kr2(p.rate)}` : ''}`;

export function PayDetail({ l }: { l: PayLine }) {
  const reasons = Object.entries(l.sickNotes).filter(([k]) => LINE_NOTES[k]);
  return (
    <div className="lpay" data-testid="pay-detail">
      <div className="lfacts">
        <Fact k={T('Type')}>{l.monthly ? T('Fastlønn') : T('Timelønn')}</Fact>
        <Fact k={T('Godkjente timer')}>{l.approvedH == null ? '–' : `${num(l.approvedH)} t`}</Fact>
        <Fact k={T('Grunnlønn')}>
          <span data-testid="pay-base">{kr(l.base)}</span> kr
        </Fact>
        <Fact k={T('Tillegg')}>
          <span data-testid="pay-supp">{kr(l.supp)}</span> kr
        </Fact>
        <Fact k={T('Overtid')}>
          <span data-testid="pay-ot">{kr(l.ot)}</span> kr
        </Fact>
        <Fact k={T('Fravær')}>
          <span data-testid="pay-abs">{kr(l.absence)}</span> kr
          {l.absenceHours > 0 && (
            <small>
              {num(l.absenceHours)} t{l.navHours > 0 ? `, ${num(l.navHours)} t NAV` : ''}
            </small>
          )}
          {l.absenceDays > 0 && (
            <small data-testid="absence-days">
              {num(l.absenceDays)} {T('dager')}
              {l.navDays > 0 ? `, ${num(l.navDays)} ${T('dager')} NAV` : ''}
              {l.shortBasis > 0 ? `, ${T('dagsats fra under tre måneder')}` : ''}
            </small>
          )}
        </Fact>
        <Fact k={T('Sum i filen')}>
          <span data-testid="pay-sum">{kr(l.total)}</span> kr
        </Fact>
      </div>
      {l.monthly && (
        <p className="lnote">
          {T(
            'Fastlønn betales i lønnssystemet: filen har ingen grunnlønnslinje. Tillegg, overtid og fravær står her.',
          )}
        </p>
      )}
      {(l.unapproved > 0 || l.open > 0) && (
        <p className="lnote warn">
          {[
            l.unapproved
              ? T('{n} stemplede dager er ikke godkjent og er ikke med.', { n: l.unapproved })
              : '',
            l.open ? T('{n} åpne stemplinger.', { n: l.open }) : '',
          ]
            .filter(Boolean)
            .join(' ')}
        </p>
      )}
      <h3>{T('Lønnsarter')}</h3>
      {l.parts.length === 0 ? (
        <p className="lnote">{T('Ingen lønnslinjer i måneden.')}</p>
      ) : (
        <ul className="lparts">
          {l.parts.map((p) => (
            <li key={p.name} data-testid={`part-${p.kind}`}>
              <div>
                <b>{p.name}</b>
                <small>
                  {T(KIND[p.kind])}, {p.code ? `${T('lønnsart')} ${p.code}` : T('lønnsart mangler')}
                  {p.paidBy === 'nav' ? `, ${T('NAV betaler')}` : ''}
                </small>
                {p.notes
                  ?.filter((n) => LINE_NOTES[n])
                  .map((n) => (
                    <small key={n}>{T(LINE_NOTES[n]?.title ?? n)}</small>
                  ))}
              </div>
              <div className="lpartn">
                <span>{workings(p)}</span>
                <b>{kr(p.total)} kr</b>
              </div>
            </li>
          ))}
        </ul>
      )}
      {(l.shortBasis > 0 || reasons.length > 0) && (
        <ul className="lreasons">
          {l.shortBasis > 0 && (
            <li>{T('Dagsats fra under tre måneder med arbeid i Timefly. Sjekk satsen.')}</li>
          )}
          {reasons.map(([k, n]) => (
            <li key={k}>
              <b>{T(LINE_NOTES[k]?.title ?? k)}</b>
              {n > 1 ? ` (${n})` : ''}: {T(LINE_NOTES[k]?.detail ?? '')}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
